import { useCallback, type KeyboardEvent, type ReactNode } from 'react';
import { useGlossary } from '../glossary/GlossaryContext';
import { getTerm } from '../glossary/terms';

// ────────────────────────────────────────────────────────────────────────────
// <GlossaryTerm term="irs_402g">402(g)</GlossaryTerm>
//
// Wraps a span of text in a calmly-styled affordance that opens the glossary
// panel pinned to that term. Discipline points:
//   - Subtle dotted underline (NOT bold, NOT colored) — a learnable signal
//     without visual noise. Color shifts on hover.
//   - A small ⓘ glyph that surfaces only on hover/focus.
//   - Keyboard-actionable (Enter / Space).
//   - If the term id is unknown, render the children as plain text and warn
//     in dev (so broken refs are caught early without exploding the page).
// ────────────────────────────────────────────────────────────────────────────

interface Props {
  /** Stable glossary term id (matches `GlossaryTerm.id` in `terms.ts`). */
  term: string;
  children: ReactNode;
  /** Override hover title (defaults to the term's `short`). */
  title?: string;
  /** Optional className passthrough (rare — for layout corrections). */
  className?: string;
}

export function GlossaryTerm({ term, children, title, className = '' }: Props) {
  const { open } = useGlossary();
  const entry = getTerm(term);

  const handleClick = useCallback(() => {
    open(term);
  }, [open, term]);

  const handleKey = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        open(term);
      }
    },
    [open, term],
  );

  // Unknown term — render plain children. Warn once in dev so we surface
  // broken refs (typos in id strings) but don't crash the page.
  if (!entry) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(`[glossary] <GlossaryTerm term="${term}"> — unknown id`);
    }
    return <span className={className}>{children}</span>;
  }

  const tip = title ?? entry.short;

  return (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={handleKey}
      title={tip}
      aria-label={`Define: ${entry.term}`}
      className={`group/glossary inline cursor-help border-b border-dotted border-ink-400 text-inherit transition hover:border-accent-500 hover:text-accent-700 focus-visible:rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 dark:border-ink-500 dark:hover:border-accent-500 dark:hover:text-accent-500 ${className}`}
    >
      {children}
      <span
        aria-hidden
        className="ml-0.5 text-[0.7em] align-super opacity-0 transition-opacity group-hover/glossary:opacity-70 group-focus-visible/glossary:opacity-70"
      >
        &#9432;
      </span>
    </button>
  );
}
